"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.NON_SPECIALIZING_DIMENSION_PREFIXES = void 0;
exports.addDirectionPrefixToLabel = addDirectionPrefixToLabel;
exports.getDefaultOperatorForDimension = getDefaultOperatorForDimension;
exports.getDimensionDirection = getDimensionDirection;
exports.getDimensionIntent = getDimensionIntent;
exports.getDirectionPrefix = getDirectionPrefix;
exports.isAgentDimension = isAgentDimension;
exports.isAgentLabelDimension = isAgentLabelDimension;
exports.isContainsDimension = isContainsDimension;
exports.isDestinationDimension = isDestinationDimension;
exports.isDeviceLabelDimension = isDeviceLabelDimension;
exports.isDirectionalDimension = isDirectionalDimension;
exports.isLabelDimension = isLabelDimension;
exports.isNonSpecializingDimension = isNonSpecializingDimension;
exports.isSourceDimension = isSourceDimension;
exports.isTestDimension = isTestDimension;
exports.isTestLabelDimension = isTestLabelDimension;
var _deviceTagMeasurements = require("../dictionary/v2/compilers/deviceTagMeasurements");
/**
 * Utility functions for detecting and working with directional dimensions.
 *
 * Dimensions can be "directional" meaning they represent either a source or destination
 * in a network flow or test. This file provides helpers to detect and label these dimensions.
 */

/**
 * Patterns that indicate a source dimension.
 * Order matters - more specific patterns should come first.
 */
const SOURCE_PATTERNS = [
// Exact prefixes
/^src_/,
// src_agent, src_as, src_eth_mac
/^i_src_/,
// i_src_as_name, i_src_connect_type_name
/^input_/,
// input_port
// Contains patterns
/_src_/,
// inet_src_addr, l4_src_port
/_input_/ // i_input_snmp_alias
];

/**
 * Patterns that indicate a destination dimension.
 * Order matters - more specific patterns should come first.
 */
const DESTINATION_PATTERNS = [
// Exact prefixes
/^dst_/,
// dst_agent, dst_as, dst_eth_mac
/^i_dst_/,
// i_dst_as_name, i_dst_connect_type_name
/^output_/,
// output_port
// Contains patterns
/_dst_/,
// inet_dst_addr, l4_dst_port
/_output_/ // i_output_snmp_alias
];

/**
 * Check if a dimension name represents a source direction.
 *
 * @param dimensionName - The dimension field name (e.g., 'src_agent', 'inet_src_addr')
 * @returns true if this is a source dimension
 *
 * @example
 * isSourceDimension('src_agent') // true
 * isSourceDimension('inet_src_addr') // true
 * isSourceDimension('IP_src') // true
 * isSourceDimension('input_port') // true
 * isSourceDimension('dst_agent') // false
 */
function isSourceDimension(dimensionName) {
  if (!dimensionName) {
    return false;
  }
  return SOURCE_PATTERNS.some(pattern => pattern.test(dimensionName));
}

/**
 * Check if a dimension name represents a destination direction.
 *
 * @param dimensionName - The dimension field name (e.g., 'dst_agent', 'inet_dst_addr')
 * @returns true if this is a destination dimension
 *
 * @example
 * isDestinationDimension('dst_agent') // true
 * isDestinationDimension('inet_dst_addr') // true
 * isDestinationDimension('IP_dst') // true
 * isDestinationDimension('output_port') // true
 * isDestinationDimension('src_agent') // false
 */
function isDestinationDimension(dimensionName) {
  if (!dimensionName) {
    return false;
  }
  return DESTINATION_PATTERNS.some(pattern => pattern.test(dimensionName));
}

/**
 * Check if a dimension is directional (either source or destination).
 *
 * @param dimensionName - The dimension field name
 * @returns true if this is a source or destination dimension
 */
function isDirectionalDimension(dimensionName) {
  return isSourceDimension(dimensionName) || isDestinationDimension(dimensionName);
}

/**
 * Get the direction of a dimension.
 *
 * @param dimensionName - The dimension field name
 * @returns 'source' | 'destination' | null
 */
function getDimensionDirection(dimensionName) {
  if (isSourceDimension(dimensionName)) {
    return 'source';
  }
  if (isDestinationDimension(dimensionName)) {
    return 'destination';
  }
  return null;
}

/**
 * Get a short direction prefix for display purposes.
 *
 * @param dimensionName - The dimension field name
 * @param useFullWord - If true, returns 'Source'/'Destination' instead of 'Src'/'Dst'
 * @returns 'Src' | 'Dst' | 'Source' | 'Destination' | null
 */
function getDirectionPrefix(dimensionName, useFullWord = false) {
  if (isSourceDimension(dimensionName)) {
    return useFullWord ? 'Source' : 'Src';
  }
  if (isDestinationDimension(dimensionName)) {
    return useFullWord ? 'Destination' : 'Dst';
  }
  return null;
}

/**
 * Add a direction prefix to a label if the dimension is directional
 * and the label doesn't already contain a direction indicator.
 *
 * @param dimensionName - The dimension field name
 * @param label - The display label for the dimension
 * @param useFullWord - If true, uses 'Source'/'Destination' instead of 'Src'/'Dst'
 * @returns The label with direction prefix if needed
 *
 * @example
 * addDirectionPrefixToLabel('src_agent', 'Agent') // 'Src Agent'
 * addDirectionPrefixToLabel('src_agent', 'Agent', true) // 'Source Agent'
 * addDirectionPrefixToLabel('src_agent', 'Source Agent') // 'Source Agent' (unchanged)
 * addDirectionPrefixToLabel('test_id', 'Test Name') // 'Test Name' (unchanged)
 */
function addDirectionPrefixToLabel(dimensionName, label, useFullWord = false) {
  if (!label) {
    return label;
  }
  const prefix = getDirectionPrefix(dimensionName, useFullWord);
  if (!prefix) {
    return label;
  }

  // Check if label already contains direction info
  const lowerLabel = label.toLowerCase();
  const hasDirectionPrefix = lowerLabel.includes('source') || lowerLabel.includes('destination') || lowerLabel.includes('src') || lowerLabel.includes('dst') || lowerLabel.includes('input') || lowerLabel.includes('output');
  if (hasDirectionPrefix) {
    return label;
  }
  return `${prefix} ${label}`;
}

/**
 * Get the UI intent color for a dimension based on its direction.
 * Source dimensions get 'warning' (orange), destination gets 'info' (blue).
 *
 * @param dimensionName - The dimension field name
 * @returns 'warning' | 'info' | 'success'
 */
function getDimensionIntent(dimensionName) {
  if (isSourceDimension(dimensionName)) {
    return 'warning';
  }
  if (isDestinationDimension(dimensionName)) {
    return 'info';
  }
  return 'success';
}

/**
 * Get the smart default operator for a dimension based on its type and naming patterns.
 *
 * @param dimensionName - The dimension field name
 * @param operatorOptions - Available operators for this dimension from dictionary
 * @returns The operator value to use as default
 */
function getDefaultOperatorForDimension(dimensionName, operatorOptions) {
  // BGP dimensions prefer regex for path/community matching
  if (dimensionName.includes('_bgp_aspath') || /_bgp_(\w+_)?community/.test(dimensionName)) {
    return '~';
  }

  // Name/description fields prefer case-insensitive like for user-friendly search
  if (dimensionName.includes('as_name') || dimensionName.includes('asn_name') || dimensionName.includes('interface_description') || dimensionName.includes('snmp_alias')) {
    return 'ILIKE';
  }

  // Default to first available operator (usually '=')
  return operatorOptions[0]?.value || '=';
}

// ---------------------------------------------------------------------------
// Synthetics dimension helpers
// ---------------------------------------------------------------------------

/** Agent-related dimension names used in synthetics queries */
const AGENT_DIMENSIONS = ['agent_id', 'src_agent', 'dst_agent', 'agent'];

/** Test-related dimension names used in synthetics queries */
const TEST_DIMENSIONS = ['test_id', 'test', 'test_name'];

/**
 * Check if dimension is an agent-related dimension.
 * Agent dimensions support equality (=, <>) and contains (ILIKE, NOT ILIKE) operators.
 */
function isAgentDimension(dimensionName) {
  return AGENT_DIMENSIONS.includes(dimensionName) || dimensionName.startsWith('agent_id_');
}

/**
 * Check if dimension is a test-related dimension.
 * Test dimensions support equality (=, <>) and contains (ILIKE, NOT ILIKE) operators.
 */
function isTestDimension(dimensionName) {
  return TEST_DIMENSIONS.includes(dimensionName) || dimensionName.startsWith('test_id_');
}

/**
 * Check if dimension supports contains operator but not regex.
 * Used for dimensions like DNS hostname.
 */
function isContainsDimension(dimensionName) {
  return dimensionName === 'dns_target_hostname' || dimensionName === 'target_hostname' || dimensionName === 'hostname';
}

// ---------------------------------------------------------------------------
// Label pseudo-dimension helpers
// ---------------------------------------------------------------------------

/** Label pseudo-dimension names used for filtering entities by label */
const LABEL_DIMENSIONS = ['ktlabel_syn_agent', 'ktlabel_syn_test', 'i_device_label'];

/** Check if dimension is a label pseudo-dimension */
function isLabelDimension(dimensionName) {
  return LABEL_DIMENSIONS.includes(dimensionName);
}

/** Check if a label dimension targets agents */
function isAgentLabelDimension(dimensionName) {
  return dimensionName === 'ktlabel_syn_agent';
}

/** Check if a label dimension targets tests */
function isTestLabelDimension(dimensionName) {
  return dimensionName === 'ktlabel_syn_test';
}

/** Check if a label dimension targets devices */
function isDeviceLabelDimension(dimensionName) {
  return dimensionName === 'i_device_label';
}

/**
 * Dimension-name prefixes that should not influence measurement specialization.
 * (e.g. dimensions belonging to shared measurements so shouldn't be used to determine which measurement to specialize for)
 */
const NON_SPECIALIZING_DIMENSION_PREFIXES = exports.NON_SPECIALIZING_DIMENSION_PREFIXES = [_deviceTagMeasurements.DEVICE_TAG_DIMENSION_PREFIX];
function isNonSpecializingDimension(dimensionName) {
  if (!dimensionName) {
    return false;
  }
  return NON_SPECIALIZING_DIMENSION_PREFIXES.some(prefix => dimensionName.startsWith(prefix));
}